import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Alert, Button, Card, Collapse, Input, Progress, Space, Tag, Typography, message } from 'antd';
import { apiClient } from '@shared/api/client';
import { isFirebaseConfigured } from '@shared/firebase';
import { useSetupStatus, SetupCheck, CheckGroup } from './useSetupStatus';
import { SETUP_GUIDES, CLOSE_PORTS_HINT } from './setupGuides';

const GROUPS: { key: CheckGroup; label: string }[] = [
  { key: 'identity', label: 'Identità' },
  { key: 'infra', label: 'Infrastruttura' },
  { key: 'access', label: 'Accessi' },
  { key: 'legal', label: 'Legale' },
  { key: 'payments', label: 'Pagamenti' },
  { key: 'store', label: 'Store' },
  { key: 'optional', label: 'Opzionali' },
];
const TAG = { ok: <Tag color="green">OK</Tag>, missing: <Tag color="orange">Da fare</Tag>, error: <Tag color="red">Errore</Tag> };

export function SetupPage(): React.ReactElement {
  const { data, isLoading, isError, refetch } = useSetupStatus();
  const [to, setTo] = useState('');
  if (isLoading) return <Card loading />;
  if (isError) {
    return (
      <Alert type="error" showIcon message="Setup non disponibile" style={{ margin: 24 }}
        action={<Button size="small" onClick={() => void refetch()}>Riprova</Button>} />
    );
  }
  // 404/403 (setup chiuso, Teramo) o utente non SUPER_ADMIN: niente da mostrare.
  if (!data) return <Navigate to="/" replace />;

  // firebase-web è una proprietà del BO (spec §5): calcolata qui, non dal backend.
  const checks: SetupCheck[] = [
    ...data.checks,
    { id: 'firebase-web', group: 'access', required: true, status: isFirebaseConfigured() ? 'ok' : 'missing' },
  ];
  const required = checks.filter((c) => c.required);
  const done = required.filter((c) => c.status === 'ok').length;
  const slug = data.tenantSlug;

  const verify = async (id: string) => {
    if (id === 'firebase-web') { window.location.reload(); return; }
    try {
      await apiClient.post(`/admin/setup/checks/${id}/verify`);
      await refetch();
    } catch {
      message.error('Verifica fallita');
    }
  };
  const testEmail = async () => {
    try { await apiClient.post('/admin/setup/test-email', { to }); message.success('Email inviata'); }
    catch { message.error('Invio fallito: controlla la voce SES'); }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%', padding: 24 }}>
      <Typography.Title level={3}>Setup istanza: {slug}</Typography.Title>
      <Progress percent={Math.round((done / required.length) * 100)} />
      <Typography.Text>{done} di {required.length} obbligatori completati</Typography.Text>

      {done === required.length && (
        <Alert type="success" showIcon message="Chiudi il setup" description={
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`sudo /opt/${slug}/bin/finish-setup.sh\n# poi dal laptop, in infra/cdk (il resto arriva da clubs/${slug}.json):\nnpx cdk deploy ClubStack-${slug} -c club=${slug} -c setupPort=0\n# poi imposta "setupPort": 0 in infra/cdk/clubs/${slug}.json e committa, così un redeploy non riapre la porta.\n# ${CLOSE_PORTS_HINT}`}</pre>
        } />
      )}

      {GROUPS.map(({ key, label }) => {
        const items = checks.filter((c) => c.group === key);
        if (items.length === 0) return null;
        return (
          <Card key={key} title={label}>
            <Collapse items={items.map((c) => {
              const g = SETUP_GUIDES[c.id];
              return {
                key: c.id,
                label: <Space>{TAG[c.status]}<span>{g?.title ?? c.id}</span>{!c.required && <Tag>opzionale</Tag>}{c.detail && <Typography.Text type={c.status === 'error' ? 'danger' : 'warning'}>{c.detail}</Typography.Text>}</Space>,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <ol>{g?.steps.map((s) => <li key={s}>{s.replaceAll('<slug>', slug)}</li>)}</ol>
                    {g?.command && <pre>{g.command({ slug })}</pre>}
                    <Space wrap>
                      {g?.links.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer">{l.label}</a>)}
                      {g?.editPath && <Link to={g.editPath}>Apri e modifica</Link>}
                      <Button size="small" onClick={() => verify(c.id)}>Verifica</Button>
                    </Space>
                    {c.id === 'ses' && (
                      <Space.Compact>
                        <Input placeholder="email di test" value={to} onChange={(e) => setTo(e.target.value)} />
                        <Button onClick={testEmail} disabled={!to}>Invia email di test</Button>
                      </Space.Compact>
                    )}
                  </Space>
                ),
              };
            })} />
          </Card>
        );
      })}
    </Space>
  );
}
